import axios from 'axios';

// Types
import type { ComputedRef, Ref } from 'vue';
import type {
  TAutomationStatus,
  TAutomatedEmailPatchSender,
} from '~/modules/opportunities/automated-email/types';
import { EAutomationMode } from '~/modules/opportunities/automated-email/types';

// Config
import {
  DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS,
} from '~/modules/opportunities/automated-email/settings/config';

// Models
import { AutomationStatusModel } from '~/modules/opportunities/automated-email/settings/models';

// Composables
import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';
import { useAutomatedEmailPatch } from '~/modules/opportunities/automated-email/composables/use-automated-email-patch';

// Utils
import {
  buildUserSettingsUpdatePayload,
  getUnconfiguredAutomationStatus,
  mapUserSettingsResponseToAutomationStatus,
} from '~/modules/opportunities/automated-email/settings/utils/user-settings-api';
import { coerceSelectableAutomationMode } from '~/modules/opportunities/automated-email/settings/utils/coerce-selectable-automation-mode';
import { coerceSelectableSemiAutomaticAutoSendDays } from '~/modules/opportunities/automated-email/settings/utils/coerce-selectable-semi-automatic-auto-send-days';

export const useAutomationStatus = () => {
  const model = new AutomationStatusModel();
  const { notifySuccess } = useNotifyResource();
  const { patchId, saleType, isLettings } = useAutomatedEmailPatch();

  const normalizeSemiAutomaticAutoSendDays = (value?: number): number =>
    coerceSelectableSemiAutomaticAutoSendDays(value, isLettings.value);

  const normalizeAutomationMode = (value: EAutomationMode): EAutomationMode =>
    coerceSelectableAutomationMode(value, isLettings.value);

  const mode: Ref<EAutomationMode> = ref<EAutomationMode>(EAutomationMode.OFF);
  const savedMode: Ref<EAutomationMode> = ref<EAutomationMode>(EAutomationMode.OFF);
  const isEnabled: Ref<boolean> = ref<boolean>(false);
  const contactFrequency: Ref<number> = ref<number>(30);
  const semiAutomaticAutoSendDays: Ref<number> = ref<number>(
    DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS
  );
  const savedSemiAutomaticAutoSendDays: Ref<number> = ref<number>(
    DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS
  );
  const senderUserId: Ref<number | null> = ref<number | null>(null);
  const savedSenderUserId: Ref<number | null> = ref<number | null>(null);
  const senderOptions: Ref<TAutomatedEmailPatchSender[]> = ref<TAutomatedEmailPatchSender[]>([]);
  const hasChangedToday: Ref<boolean> = ref<boolean>(false);
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const isSaving: Ref<boolean> = ref<boolean>(false);

  const setMode = (value: EAutomationMode): void => {
    mode.value = value;
  };

  const setIsEnabled = (value: boolean): void => {
    isEnabled.value = value;
  };

  const setIsLoading = (value: boolean): void => {
    isLoading.value = value;
  };

  const setIsSaving = (value: boolean): void => {
    isSaving.value = value;
  };

  const setChangedToday = (value: boolean): void => {
    hasChangedToday.value = value;
  };

  const setContactFrequency = (value: number): void => {
    contactFrequency.value = value;
  };

  const setSavedMode = (value: EAutomationMode): void => {
    savedMode.value = value;
  };

  const setSemiAutomaticAutoSendDays = (value: number): void => {
    semiAutomaticAutoSendDays.value = value;
  };

  const coerceLettingsRestrictedSelection = (): void => {
    if (!isLettings.value) {
      return;
    }

    setMode(normalizeAutomationMode(mode.value));
    setSemiAutomaticAutoSendDays(
      normalizeSemiAutomaticAutoSendDays(semiAutomaticAutoSendDays.value)
    );
  };

  const setSavedSemiAutomaticAutoSendDays = (value: number): void => {
    savedSemiAutomaticAutoSendDays.value = value;
  };

  const setSenderUserId = (value: number | null): void => {
    senderUserId.value = value;
  };

  const setSavedSenderUserId = (value: number | null): void => {
    savedSenderUserId.value = value;
  };

  const setSenderOptions = (value: TAutomatedEmailPatchSender[]): void => {
    senderOptions.value = value;
  };

  const resolveSenderUserId = (preferred: number | null): number | null => {
    if (
      preferred !== null &&
      senderOptions.value.some((sender: TAutomatedEmailPatchSender) => sender.id === preferred)
    ) {
      return preferred;
    }

    return senderOptions.value[0]?.id ?? null;
  };

  const applyAutomationStatus = (status: TAutomationStatus): void => {
    const nextMode: EAutomationMode = normalizeAutomationMode(status.mode);
    const nextSemiDays: number = normalizeSemiAutomaticAutoSendDays(
      status.semi_automatic_auto_send_days
    );

    setMode(nextMode);
    setSavedMode(nextMode);
    setIsEnabled(status.is_enabled);
    setContactFrequency(status.contact_frequency);
    setSemiAutomaticAutoSendDays(nextSemiDays);
    setSavedSemiAutomaticAutoSendDays(nextSemiDays);

    const nextSenderUserId: number | null = resolveSenderUserId(status.sender_user_id);

    setSenderUserId(nextSenderUserId);
    setSavedSenderUserId(nextSenderUserId);
    setChangedToday(status.has_changed_today);
  };

  const fetchSenderOptions = async (): Promise<void> => {
    if (patchId.value === undefined) {
      setSenderOptions([]);

      return;
    }

    setSenderOptions(await model.getPatchUsers(patchId.value, saleType.value));
  };

  const fetchStatus = async (): Promise<void> => {
    if (patchId.value === undefined) {
      setSenderOptions([]);
      applyAutomationStatus(getUnconfiguredAutomationStatus());

      return;
    }

    setIsLoading(true);

    try {
      await fetchSenderOptions();

      const status: TAutomationStatus = mapUserSettingsResponseToAutomationStatus(
        await model.getPatchSettings(patchId.value, saleType.value)
      );

      applyAutomationStatus(status);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        applyAutomationStatus(getUnconfiguredAutomationStatus());
      } else {
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isAutomationModeDirty: ComputedRef<boolean> = computed<boolean>(
    () =>
      mode.value !== savedMode.value ||
      semiAutomaticAutoSendDays.value !== savedSemiAutomaticAutoSendDays.value
  );

  const saveStatus = async (): Promise<void> => {
    if (patchId.value === undefined || senderUserId.value === null) {
      return;
    }

    setIsSaving(true);

    const normalizedSemiDays: number = normalizeSemiAutomaticAutoSendDays(
      semiAutomaticAutoSendDays.value
    );

    try {
      const status: TAutomationStatus = mapUserSettingsResponseToAutomationStatus(
        await model.updatePatchSettings(
          buildUserSettingsUpdatePayload(
            patchId.value,
            saleType.value,
            mode.value,
            savedMode.value,
            contactFrequency.value,
            normalizedSemiDays,
            senderUserId.value
          )
        )
      );

      applyAutomationStatus(status);

      notifySuccess();
    } finally {
      setIsSaving(false);
    }
  };

  watch(isLettings, (): void => {
    coerceLettingsRestrictedSelection();
  });

  return {
    patchId,
    saleType,
    isLettings,
    mode,
    semiAutomaticAutoSendDays,
    senderUserId,
    senderOptions,
    isAutomationModeDirty,
    isEnabled,
    contactFrequency,
    isModeDisabled: hasChangedToday,
    isLoading,
    isSaving,
    fetchStatus,
    saveStatus,
  };
};
